import { yupResolver } from "@hookform/resolvers/yup";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  Grid,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { toIsoLocalString } from "qubCommon/helperMethods/datetimeConvert";
import { DateTimeInput, Dropdown } from "qubCommon/Inputs";
import CurrencyInput from "qubCommon/Inputs/CurrencyInput";
import TextInput from "qubCommon/Inputs/TextInput";
import { formatFloat, showFloat } from "qubCommon/wijmoHelpers/numberMethods";
import { useEffect } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { applicationTypeSeasonSchema } from "util/validations/application-type-season";

const ApplicationTypeSeasonDialog = ({
  open,
  onClose,
  onSubmit,
  defaultValues = {},
  seasons = [],
  applicationTypes = [],
}) => {
  console.log(applicationTypes);
  const methods = useForm({
    defaultValues,
    resolver: yupResolver(applicationTypeSeasonSchema),
  });
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const isEdit = !!defaultValues?.id;
  const handleClose = () => {
    onClose();
    methods.reset();
  };

  // Reset form when editing a different row

  useEffect(() => {
    if (open) {
      methods.reset({
        description: "",
        abbr: "",
        dateFrom: null,
        dateTo: null,
        decision: "",
        ...defaultValues,
      });
    }
  }, [defaultValues, open, methods.reset]);

  const handleSubmit = (data) => {
    onSubmit({
      id: defaultValues?.id || null,
      typeId: data.typeId,
      seasonId: data.seasonId,
      limit: Number(data.limit),
      amount: data.amount,
      dateFrom: toIsoLocalString(data.dateFrom),
      dateTo: toIsoLocalString(data.dateTo),
      paused: !!data.paused,
      diasCode: data.diasCode,
      diasFee: data.diasFee,
      diasDataKey: data.diasDataKey,
    });
    handleClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : 3,
          p: 0,
          overflow: "hidden",
        },
      }}
    >
      {/* Header */}
      <Box sx={{ backgroundColor: "#f5f8fa", px: 3, py: 2 }}>
        <Typography variant="h4" fontWeight={700} color="primary">
          {isEdit
            ? "Επεξεργασία Περιόδου Είδους Αίτησης"
            : "Νέα Περίοδος Είδους Αίτησης"}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Συμπληρώστε τα στοιχεία παρακάτω.
        </Typography>
      </Box>

      <Divider />

      {/* Form */}
      <FormProvider {...methods}>
        <DialogContent sx={{ px: 3, py: 3, backgroundColor: "#fff" }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Dropdown
                name="typeId"
                label="Είδος Αίτησης"
                isFullWidth
                options={applicationTypes}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Dropdown
                name="seasonId"
                label="Περίοδος"
                isFullWidth
                options={seasons}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextInput name="limit" label="Όριο Αιτήσεων" isFullWidth />
            </Grid>

            <Grid item xs={12} sm={6}>
              <CurrencyInput
                name="amount"
                label="Ποσό (€)"
                onBlur={(e) => {
                  if (e.target.value === "") methods.setValue("amount", null);
                  else
                    methods.setValue(
                      "amount",
                      showFloat(formatFloat(e.target.value))
                    );
                }}
                isFullWidth
                defaultFromProps={false}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextInput
                name="diasCode"
                label="Αρ. Πληρωμής ΔΙΑΣ"
                isFullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextInput
                name="diasDataKey"
                label="Κωδικοποίηση ΔΙΑΣ"
                isFullWidth
              />
            </Grid>
            <Grid container item xs={12} spacing={2}>
              <Grid item xs={6}>
                <CurrencyInput
                  name="diasFee"
                  label="Προμήθεια ΔΙΑΣ"
                  onBlur={(e) => {
                    if (e.target.value === "")
                      methods.setValue("diasFee", null);
                    else
                      methods.setValue(
                        "diasFee",
                        showFloat(formatFloat(e.target.value))
                      );
                  }}
                  isFullWidth
                  defaultFromProps={false}
                />
              </Grid>
            </Grid>

            <Grid item xs={12} sm={6}>
              <DateTimeInput
                name="dateFrom"
                label="Έναρξη"
                isFullWidth
                defaultFromProps={false}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <DateTimeInput
                name="dateTo"
                label="Λήξη"
                isFullWidth
                defaultFromProps={false}
              />
            </Grid>
          </Grid>
        </DialogContent>

        <Divider />

        {/* Actions */}
        <DialogActions
          sx={{
            backgroundColor: "#f9f9f9",
            py: 2,
            px: 3,
            justifyContent: "space-between",
            flexWrap: "wrap",
          }}
        >
          <Button onClick={handleClose} variant="contained" color="error">
            Ακύρωση
          </Button>

          <Button
            variant="contained"
            color="primary"
            onClick={methods.handleSubmit(handleSubmit)}
            sx={{ px: 3 }}
          >
            {isEdit ? "Αποθήκευση Αλλαγών" : "Αποθήκευση"}
          </Button>
        </DialogActions>
      </FormProvider>
    </Dialog>
  );
};

export default ApplicationTypeSeasonDialog;
